// Step 7 — Success/checklist (T17). Polled de Subscriptions-doc via een
// onSnapshot-listener tot de Stripe-webhook `status === 'active'` zet.
//
// Dev-preview: simuleert success na 1s, geen real listener.

import { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { OnboardingFlow as OnboardingFlowContext } from '../../../StateManagment/OnboardingFlow'
import { db } from '../../../firebase/config'
import useDevPreview from '../../../hooks/useDevPreview'

const Step7Success = ({ onPrev }) => {

    const { compagnyId, setCompagnyId, clearOnboardingState } = useContext(OnboardingFlowContext)
    const history = useHistory()
    const location = useLocation()

    // Stripe-checkout-redirect rehydration: in-memory context is wiped by
    // the round-trip through stripe.com. Step 6 appends ?compagnyId=... to
    // the success_url so we can restore it here on mount.
    useEffect(() => {
        if (compagnyId) return
        const fromUrl = new URLSearchParams(location.search).get('compagnyId')
        if (fromUrl) setCompagnyId(fromUrl)
    }, [compagnyId, location.search, setCompagnyId])
    const [status, setStatus] = useState('waiting')   // 'waiting' | 'confirmed' | 'error'
    const [error, setError] = useState('')
    const [finishing, setFinishing] = useState(false)

    const { active: devPreview } = useDevPreview()

    useEffect(() => {

        // Dev-preview: na 1s mock-success
        if (devPreview) {
            const timer = setTimeout(() => setStatus('confirmed'), 1000)
            return () => clearTimeout(timer)
        }

        if (!compagnyId) {
            // Don't lock to 'error' yet — the rehydrate effect above may set
            // compagnyId from the URL on the next tick. Only show the error
            // when both context AND URL fail to provide a compagnyId.
            const hasUrlCompagnyId = !!new URLSearchParams(location.search).get('compagnyId')
            if (!hasUrlCompagnyId) {
                setError('Geen actieve onboarding-sessie.')
                setStatus('error')
            }
            return
        }

        // Rehydrate kan zojuist gebeurd zijn — reset transient error-state
        // van de eerste render zodat de listener UI niet vast blijft staan.
        setStatus('waiting')
        setError('')

        // Live listener op Subscriptions-doc voor webhook-bevestiging
        const unsub = db.collection('Subscriptions')
            .where('CompagnyID', '==', compagnyId)
            .limit(1)
            .onSnapshot(
                (snap) => {
                    if (snap.empty) return
                    const sub = snap.docs[0].data()
                    if (sub.status === 'active') {
                        setStatus('confirmed')
                    }
                },
                (err) => {
                    console.error('Step7Success listener error', err)
                    setError(err.message || 'Kon de betaalstatus niet ophalen.')
                    setStatus('error')
                }
            )

        return () => unsub()

    }, [compagnyId, devPreview])

    const goToDashboard = async () => {

        setError('')

        if (devPreview) return

        if (!compagnyId) {
            setError('Geen actieve onboarding-sessie.')
            return
        }

        setFinishing(true)
        try {
            const metaSnap = await db.collection('CompagnyMeta')
                .where('CompagnyID', '==', compagnyId)
                .limit(1)
                .get()

            if (!metaSnap.empty) {
                await metaSnap.docs[0].ref.update({
                    OnboardingStatus: { lastStep: 7, completed: true },
                })
            }

            // Onboarding is klaar — gooi de in-progress sessie weg (zowel
            // in-memory context als de localStorage-snapshot) zodat een
            // volgende /signup-bezoek niet met stale modules/compagnyId start.
            clearOnboardingState()

            // Hard nav i.p.v. history.push: `client` in hooks/Client.js is
            // module-level (zie pitfalls), dus zonder reload zou /<compagnyId>
            // niet matchen op `<Route exact path="/{client}">` (`client` blijft
            // 'signup'). Auth.js zou de user vervolgens onterecht naar /login
            // sturen, en dat is een omweg langs het inlogscherm die niemand
            // nodig heeft. window.location.href forceert een page-reload,
            // `client` evalueert opnieuw, en Auth.js vindt de user direct als
            // member.
            window.location.href = `/${compagnyId}/dashboard`
        } catch (err) {
            console.error('Step7Success finish error', err)
            setError(err.message || 'Er ging iets mis. Probeer het opnieuw.')
            setFinishing(false)
        }
    }

    return (
        <div className="onboarding-step">
            {status === 'waiting' && (
                <>
                    <h1>We bevestigen je betaling...</h1>
                    <p className="onboarding-step-intro">
                        Dit duurt meestal een paar seconden. Sluit deze pagina niet — we
                        ronden je account automatisch af zodra we alles hebben klaargezet.
                    </p>
                    <div className="onboarding-success-spinner" aria-hidden="true">
                        <div className="onboarding-success-dot" />
                        <div className="onboarding-success-dot" />
                        <div className="onboarding-success-dot" />
                    </div>
                </>
            )}

            {status === 'confirmed' && (
                <>
                    <h1>Account aangemaakt</h1>
                    <p className="onboarding-step-intro">
                        Top — je betaling is bevestigd. Je omgeving is klaar om gebruikt te worden.
                    </p>
                    <div className="onboarding-success-checklist">
                        <p>✓ Account aangemaakt</p>
                        <p>✓ Organisatie ingesteld</p>
                        <p>✓ Modules geactiveerd</p>
                        <p>✓ Betaling bevestigd</p>
                    </div>
                    {error && <p className="onboarding-error">{error}</p>}
                    <div className="onboarding-step-actions">
                        <button type="button" onClick={goToDashboard} disabled={finishing}>
                            {finishing ? <span className="onboarding-button-spinner" aria-label="Bezig" /> : 'Naar mijn omgeving'}
                        </button>
                    </div>
                </>
            )}

            {status === 'error' && (
                <>
                    <h1>Er ging iets mis</h1>
                    {error && <p className="onboarding-error">{error}</p>}
                    <div className="onboarding-step-actions">
                        <button
                            type="button"
                            className="onboarding-button-secondary"
                            onClick={onPrev}
                        >
                            Terug
                        </button>
                    </div>
                </>
            )}
        </div>
    )

}

export default Step7Success
